import type { definitions } from '@luxuryescapes/contract-svc-promo'
import type {
  IBannerGroupFields,
  IComponentCampaignHeroFields,
  IComponentContentModalFields,
  IComponentCardCarouselFields,
  IComponentCruiseOfferListFields,
  IComponentExperienceOfferListCarouselFields,
  IComponentFlightDealsCarousalFields,
  IComponentFormFields,
  IComponentHeroCarouselFields,
  IComponentHeroImageFields,
  IComponentIframeFields,
  IComponentImageTileCarouselFields,
  IComponentLogoCarouselFields,
  IComponentOfferGridViewFields,
  IComponentOfferListCarouselFields,
  IComponentReviewsFields,
  IComponentSpacerFields,
  IComponentSupportFields,
  IComponentTileNavigationFields,
  IComponentValuePropositionBannerFields,
  IComponentVerticalOfferListFields,
  IComponentVerticalOfferListsGroupFields,
  IComponentVideoBlockFields,
  IComponentWingsFields,
  ILegalPagesFields,
  IPageWebFields,
} from '@luxuryescapes/contract-svc-content'
import { ANYWHERE_PLACE_ID } from 'constants/search'
import { unique } from 'lib/array/arrayUtils'
import { getCurrentIntl } from 'i18n/util/currentIntl'
import { renderMarkdown } from 'lib/string/markdownUtils'

type Flattened<T> = T & { contentTypeId: string }

type FlattenedEntry<TFields, TContentTypeId extends string> = TFields & {
  contentTypeId: TContentTypeId
}

type ContentfulHeroImageFields = IComponentHeroImageFields & {
  desktopImageId?: string
  mobileImageId?: string
  overlayImageId?: string
  mode?: App.ComponentHeroImage['mode']
  overlayImagePosition?: App.ComponentHeroImage['overlayPosition']
  overlayPosition?: App.ComponentHeroImage['overlayPosition']
}

type ContentfulHeroCarouselFields = IComponentHeroCarouselFields & {
  heroImageTitle?: string
  heroImageSubtitle?: string
  heroImageId?: string
  offerIds?: Array<string>
}

type ContentfulExperienceOfferListCarouselFields = IComponentExperienceOfferListCarouselFields & {
  offerIds?: Array<string>
}

type ContentfulOfferListCarouselFields = IComponentOfferListCarouselFields & {
  offerIds?: Array<string>
}

type ContentfulFlightDealsCarouselFields = IComponentFlightDealsCarousalFields & {
  destinationAirportCodes?: Array<string>
}

type ContentfulVerticalOfferListFields = IComponentVerticalOfferListFields & {
  offerIds?: Array<string>
}

type ContentfulOfferGridViewFields = IComponentOfferGridViewFields & {
  offerIds?: Array<string>
}

type ContentfulCruiseOfferListFields = IComponentCruiseOfferListFields & {
  offerIds?: Array<string>
}

type ContentfulCampaignHeroFields = IComponentCampaignHeroFields & {
  linkTo?: string
}

type ContentfulWebPageBaseFields = Omit<
  IPageWebFields,
  'title' | 'seoTitle' | 'seoDescription' | 'slug' | 'components' | 'modalComponents'
> & {
  title?: string
  seoTitle?: string
  seoDescription?: string
  socialImageId?: string
  socialImageAlt?: string
  slug?: string
}

type ContentfulWebPageComponentData =
  | FlattenedEntry<IComponentCardCarouselFields, 'componentCardCarousel'>
  | FlattenedEntry<ContentfulOfferListCarouselFields, 'componentOfferListCarousel'>
  | FlattenedEntry<IComponentSupportFields, 'componentSupport'>
  | FlattenedEntry<IComponentValuePropositionBannerFields, 'componentValuePropositionBanner'>
  | FlattenedEntry<ContentfulExperienceOfferListCarouselFields, 'componentExperienceOfferListCarousel'>
  | FlattenedEntry<ContentfulHeroCarouselFields, 'componentHeroCarousel'>
  | FlattenedEntry<IComponentTileNavigationFields, 'componentTileNavigation'>
  | FlattenedEntry<IComponentWingsFields, 'componentWings'>
  | FlattenedEntry<IComponentVideoBlockFields, 'componentVideoBlock'>
  | FlattenedEntry<IComponentReviewsFields, 'componentReviews'>
  | FlattenedEntry<IComponentImageTileCarouselFields, 'componentImageTileCarousel'>
  | FlattenedEntry<ContentfulHeroImageFields, 'componentHeroImage'>
  | FlattenedEntry<ILegalPagesFields, 'legalPages'>
  | FlattenedEntry<IComponentIframeFields, 'componentIframe'>
  | FlattenedEntry<IComponentLogoCarouselFields, 'componentLogoCarousel'>
  | FlattenedEntry<ContentfulFlightDealsCarouselFields, 'componentFlightDealsCarousal'>
  | FlattenedEntry<IComponentFormFields, 'componentForm'>
  | FlattenedEntry<ContentfulVerticalOfferListFields, 'componentVerticalOfferList'>
  | FlattenedEntry<IComponentVerticalOfferListsGroupFields, 'componentVerticalOfferListsGroup'>
  | FlattenedEntry<IComponentSpacerFields, 'componentSpacer'>
  | FlattenedEntry<ContentfulCampaignHeroFields, 'componentCampaignHero'>
  | FlattenedEntry<IBannerGroupFields, 'bannerGroup'>
  | FlattenedEntry<ContentfulOfferGridViewFields, 'componentOfferGridView'>
  | FlattenedEntry<ContentfulCruiseOfferListFields, 'componentCruiseOfferList'>

type ContentfulWebPageModalComponentData =
  | FlattenedEntry<IComponentFormFields, 'componentForm'>
  | FlattenedEntry<IComponentContentModalFields, 'componentContentModal'>

export type ContentfulWebPageResponseFields = ContentfulWebPageBaseFields & {
  allowedRegions?: Array<string>
  components?: Array<ContentfulWebPageComponentData>
  modalComponents?: Array<ContentfulWebPageModalComponentData>
}

function getUnknownComponentTitle(): string {
  const intl = getCurrentIntl()

  return intl.formatMessage({
    id: 'JdLa4JpQX4E5ec7eWqAq',
    defaultMessage: 'Component not found',
    description: 'Fallback title shown when a marketing page references an unsupported component type',
  })
}

function mapCompetitionGrandPrize(grandPrize: definitions['CompetitionConfigV2']['grandPrizeDetails']): App.MarketingGrandPrizeDetail {
  return {
    offerId: grandPrize.offerId,
    title: grandPrize.title,
    link: grandPrize.link,
    includes: grandPrize.includes.map(include => include) ?? [],
    offer: {
      description: grandPrize.offer.description,
      name: grandPrize.offer.name,
      subtitle: grandPrize.offer.subtitle,
      image: {
        id: grandPrize.offer.imageId,
      },
    },
  }
}

function mapCompetitionHeadData(headData: any) {
  return {
    thumbnailImage: { id: headData.thumbnailImageId },
  }
}

function mapCompetitionPayoutConfig(payout: definitions['PayoutConfigV2']): definitions['PayoutConfigV2'] {
  return {
    id: payout.id,
    weightedChance: payout.weightedChance,
    value: payout.value,
  }
}

function mapMinorPrizeDetails(prizeDetail: any): App.MarketingCompetitionMinorPrizeDetails {
  return {
    codeName: prizeDetail.codeName,
    promoId: prizeDetail.promoIde,
    promoType: prizeDetail.promoType,
    expiryDate: prizeDetail.expiryDate,
    discountValue: prizeDetail.discountValue,
    minSpend: prizeDetail.minSpend,
    maxDiscount: prizeDetail.maxDisscount,
  }
}

export function mapCompetitionConfig(data: definitions['CompetitionConfigV2']): App.MarketingCompetitionConfig {
  return {
    allowedRegions: data.allowedRegions.map(region => region) ?? [],
    episodeId: data.episodeId ?? undefined,
    prizeType: data.prizeType,
    drawFrequency: data.drawFrequency,
    startDateUtc: data.startDateUtc,
    endDateUtc: data.endDateUtc,
    drawDateUtc: data.drawDateUtc,
    heroImage: { id: data.heroImageId },
    heroTextIntro: data.heroTextIntro,
    heroTextHighlight: data.heroTextHighlight,
    promoLink: data.promo_link,
    faqSlug: data.faqSlug,
    payoutConfig: data.payoutConfig.map(mapCompetitionPayoutConfig) ?? [],
    images: data.images.map(image => ({
      id: image.imageId,
      title: image.imageTitle,
    })),
    grandPrizeDetails: mapCompetitionGrandPrize(data.grandPrizeDetails),
    prizeContactDateUtc: data.prizeContactDateUtc,
    header: data.header,
    title: data.title,
    bodyText: data.bodyText,
    footerText: data.footerText,
    breadcrumb: data.breadcrumb,
    headData: mapCompetitionHeadData(data.headData),
  }
}

export function mapCompetitionMinorPrize(data: any): App.MarketingCompetitionMinorPrize {
  return {
    prizeType: data.prizeType,
    drawFrequency: data.drawFrequency,
    prizeValue: data.prizeValue,
    prizeDetails: data.prizeDetails ? mapMinorPrizeDetails(data.prizeDetails) : undefined,
  }
}

export function mapCompetitionUserStatus(data: any): App.MarketingCompetitionUserStatus {
  if (Object.keys(data.prizeDetails).length === 0 || !data.alreadyHasReward) {
    return {
      alreadyHasReward: data.alreadyHasReward,
      prizeDetails: null,
    }
  }
  if (data.prizeDetails.competitionType === 'promo') {
    return {
      alreadyHasReward: data.alreadyHasReward,
      ...mapCompetitionStatusPromoPrize(data.prizeDetails),
    }
  }
  return {
    alreadyHasReward: data.alreadyHasReward,
    competitionType: 'raffle',
    prizeValue: data.prizeDetails.prizeValue,
  }
}

export function mapContentfulWebPageData(data: ContentfulWebPageResponseFields): App.ContentfulWebPageData {
  return {
    title: data.title ?? '',
    destination: data.slug,
    seoTitle: data.seoTitle,
    seoDescription: data.seoDescription,
    socialImageId: data.socialImageId,
    socialImageAlt: data.socialImageAlt,
    allowedRegions: data.allowedRegions?.map((region: string) => region) ?? [],
    components: (data.components ?? []).map(mapContentfulWebPageComponent),
    modalComponents: data.modalComponents?.map(mapContentfulWebPageModalComponent),
  }
}

export function mapContentfulNavBar(data: any): Array<App.ContentfulNavBarItem> {
  return data.navBar ? data.navBar.map((item: any) => ({
    title: item.fields.title,
    subtitle: item.fields.subtitle,
    to: item.fields.linkTo,
  })) : []
}

function mapHeroMode(value: unknown): App.ComponentHeroImage['mode'] {
  return value === 'image-overlay' ? 'image-overlay' : 'text'
}

function mapOverlayPosition(value: unknown): App.ComponentHeroImage['overlayPosition'] {
  if (value === 'left' || value === 'right' || value === 'center') {
    return value
  }

  return 'center'
}

export function mapContentfulWebPageComponent(data: ContentfulWebPageComponentData): App.ContentfulWebPageComponent {
  const title: string = ('title' in data ? data.title : undefined) ?? ''
  switch (data.contentTypeId) {
    case 'componentHeroImage':
      const mode = mapHeroMode(data.mode)
      const fallbackImageId = data.imageId
      const desktopImageId = data.desktopImageId ?? data.mobileImageId ?? fallbackImageId
      const mobileImageId = data.mobileImageId ?? data.desktopImageId ?? fallbackImageId

      if (
        mode === 'image-overlay'
        && !desktopImageId
        && !mobileImageId
      ) {
        console.warn('componentHeroImage is in image-overlay mode but missing desktopImageId/mobileImageId')
      }

      return {
        title,
        subtitle: data.subtitle,
        image: fallbackImageId ? { id: fallbackImageId } : undefined,
        desktopImage: desktopImageId ? { id: desktopImageId } : undefined,
        mobileImage: mobileImageId ? { id: mobileImageId } : undefined,
        overlayImage: data.overlayImageId ? { id: data.overlayImageId } : undefined,
        contentTypeId: data.contentTypeId,
        mode,
        overlayPosition: mapOverlayPosition(data.overlayImagePosition ?? data.overlayPosition),
        toggle: data.toggle,
        navBar: mapContentfulNavBar(data),
        type: data.types,
      }
    case 'componentHeroCarousel':
      return {
        title,
        heroImageTitle: data.heroImageTitle ?? '',
        subtitle: data.heroImageSubtitle ?? '',
        image: { id: data.heroImageId ?? '' },
        contentTypeId: data.contentTypeId,
        filters: {
          offerTypes: data.offerTypes ? data.offerTypes.map((offerType: string) => offerType) as App.OfferListFilters['offerTypes'] : [],
          placeIds: data.placeIDs && data.placeIDs.length > 0 ? data.placeIDs.map((placeId: string) => placeId) : [ANYWHERE_PLACE_ID],
          campaigns: data.campaigns ? data.campaigns.map((campaign: string) => campaign) : [],
          limit: data.limit,
        },
        offerIds: data.offerIds?.length ? data.offerIds : undefined,
      }
    case 'componentCardCarousel':
      return {
        title,
        subtitle: data.subtitle,
        cardItems: (data.cardItems ?? []).map(mapContentfulWebPageCardItem),
        viewAllLink: data.viewAllLink,
        isLargeContentWrapper: data.isLargeContentWrapper,
        contentTypeId: data.contentTypeId,
      }
    case 'componentFlightDealsCarousal':
      return {
        title,
        category: data.category,
        originAirportCodes: data.originAirportCodes ?? [],
        // `destinationAirportCode` is the legacy single-value field, kept so existing entries keep working
        destinationAirportCodes: unique([
          ...(data.destinationAirportCodes ?? []),
          ...(data.destinationAirportCode ? [data.destinationAirportCode] : []),
        ]),
        viewAllLink: data.viewAllLink,
        contentTypeId: data.contentTypeId,
      }
    case 'componentOfferListCarousel':
      return {
        title,
        filters: {
          offerTypes: data.offerTypes ? data.offerTypes.map((offerType: string) => offerType) as App.OfferListFilters['offerTypes'] : [],
          campaigns: data.campaigns ? data.campaigns.map((campaign: string) => campaign) : [],
          placeIds: data.placeIds && data.placeIds.length > 0 ? data.placeIds.map((placeId: string) => placeId) : [ANYWHERE_PLACE_ID],
          limit: data.offersToShow,
        },
        viewAllLink: data.viewAllLink,
        contentTypeId: data.contentTypeId,
        lookingForSomethingElseOption: data.lookingForSomethingElseLink,
        showFilters: data.showFilters ?? true,
        offerIds: data.offerIds?.length ? data.offerIds : undefined,
      }
    case 'componentSupport':
      return {
        contentTypeId: data.contentTypeId,
        title,
      }
    case 'componentValuePropositionBanner':
      return {
        contentTypeId: data.contentTypeId,
        title,
        items: (data.items ?? []).map(mapComponentValuePropositionItems),
      }
    case 'componentExperienceOfferListCarousel':
      return {
        title,
        destination: data.destination,
        contentTypeId: data.contentTypeId,
        campaigns: data.campaigns ?? [],
        offerIds: data.offerIds?.length ? data.offerIds : undefined,
      }
    case 'componentTileNavigation':
      return {
        title,
        tiles: (data.tiles ?? []).map((tile: any) => tile.fields),
        maxTilesVisible: data.maxTilesVisible ?? 0,
        variant: data.variant,
        contentTypeId: data.contentTypeId,
      }
    case 'componentWings':
      return {
        title: data.slug ?? '',
        banners: (data.placements ?? []).map((placement: any) => ({
          name: placement.fields.name,
          bannerId: placement.fields.bannerId,
          imageId: placement.fields.desktopImageId,
          imageAlt: placement.fields.desktopImageId,
          position: 'left',
          href: placement.fields.linkTo,
          desktopImageId: placement.fields.desktopImageId,
          mobileImageId: placement.fields.mobileImageId,
        })),
        contentTypeId: data.contentTypeId,
      }
    case 'componentVideoBlock':
      return mapContentfulVideoBlock(data)
    case 'componentReviews':
      return {
        contentTypeId: data.contentTypeId,
        title,
      }
    case 'componentImageTileCarousel':
      return {
        contentTypeId: data.contentTypeId,
        title,
        showTitle: data.showTitle || data.showTitle === undefined,
        tiles: (data.tiles ?? []).map(mapContentfulWebPageImageTile),
      }
    case 'componentVerticalOfferList':
      return {
        contentTypeId: data.contentTypeId,
        title,
        showFilters: data.showFilters ?? false,
        showSearchHeader: data.showSearchHeader || data.showSearchHeader === undefined,
        campaigns: data.campaigns ? data.campaigns : [],
        offerTypes: data.offerTypes ? data.offerTypes as App.ComponentVerticalOfferList['offerTypes'] : [],
        placeIds: data.placeIds ? data.placeIds : [],
        isInfiniteScroll: data.isInfiniteScroll ?? false,
        offerIds: data.offerIds?.length ? data.offerIds : undefined,
      }
    case 'legalPages':
      return {
        contentTypeId: 'componentFaqs',
        content: (data.pageContent ?? []).map((content: any) => ({
          answer: content.fields.answer,
          question: content.fields.title,
        })),
        title,
      }
    case 'componentIframe':
      return {
        contentTypeId: 'componentIframe',
        title,
        url: data.url,
        frameHeight: data.frameHeight,
        mobileFrameHeight: data.mobileFrameHeight,
      }
    case 'componentLogoCarousel':
      return {
        contentTypeId: 'componentLogoCarousel',
        images: (data.images ?? []).map((image: any) => ({
          title: image.fields.title,
          id: image.fields.id,
        })),
        title,
        backgroundColor: data.backgroundColor || 'white',
        withOutline: data.withOutline,
      }
    case 'componentForm':
      return mapComponentForm(data)
    case 'componentVerticalOfferListsGroup': {
      return {
        contentTypeId: 'componentVerticalOfferListsGroup',
        title,
        offerListsGroup: (data.offerListsGroup ?? []).map((payload: any) => ({
          location: payload.fields.location,
          offerType: payload.fields.offerType,
        })),
      }
    }
    case 'componentSpacer': {
      return {
        title,
        contentTypeId: 'componentSpacer',
        space: data.space,
        backgroundColor: data.backgroundColor as App.ComponentSpacer['backgroundColor'],
      }
    }
    case 'componentCampaignHero': {
      return {
        contentTypeId: 'componentCampaignHero',
        title,
        backGroundImage: { id: data.backgroundImageId },
        linkTo: data.linkTo ?? '',
        bodyText: data.bodyText,
        footerText: data.footerText,
        promoCode: data.promoCode,
        logoImageId: { id: data.logoImageId },
        labels: data.labels ? data.labels as Array<App.ContentfulCampaignHeroLabels> : [],
      }
    }
    case 'bannerGroup':
      return {
        contentTypeId: 'componentBannerGroup',
        title,
        subtitle: data.subtitle,
        banners: (data.banners ?? []).map(mapDynamicBanner),
        slug: data.slug,
      }
    case 'componentOfferGridView':
      return {
        contentTypeId: 'componentOfferGridView',
        title,
        placeIds: data.placeIds ? data.placeIds.map((placeId: string) => placeId) : [ANYWHERE_PLACE_ID],
        offerTypes: data.offerTypes ? data.offerTypes as App.ComponentOfferGridView['offerTypes'] : [],
        campaigns: data.campaigns ? data.campaigns.map((campaign: string) => campaign) : [],
        offersToShow: data.offersToShow,
        offerIds: data.offerIds?.length ? data.offerIds : undefined,
      }
    case 'componentCruiseOfferList':
      return {
        contentTypeId: 'componentCruiseOfferList',
        title,
        destinationPlaceIds: data.destinationPlaceIds ? data.destinationPlaceIds.map((destinationPlaceId: string) => destinationPlaceId) : [],
        departurePlaceIds: data.departurePlaceIds ? data.departurePlaceIds.map((departurePlaceId: string) => departurePlaceId) : [],
        cruiseLines: data.cruiseLines ? data.cruiseLines.map((cruiseLine: string) => cruiseLine) : [],
        durationMin: data.minDuration,
        durationMax: data.maxDuration,
        departureMonths: data.departureMonths ? data.departureMonths.map((departureMonth: string) => departureMonth) : [],
        viewType: data.viewType || 'list',
        offerIds: data.offerIds?.length ? data.offerIds : undefined,
      }
    default:
      return {
        title: getUnknownComponentTitle(),
        contentTypeId: 'componentNotFound',
      }
  }
}

export function mapContentfulVideoBlock(data: Flattened<IComponentVideoBlockFields>): App.ComponentVideoBlock {
  return {
    caption: data.caption,
    title: data.title ?? '',
    subtitle: data.subtitle,
    bodyText: data.bodyText ?? '',
    contentAlign: data.contentAlign || 'start',
    contentPosition: data.contentPosition,
    videoId: data.videoId,
    contentTypeId: 'componentVideoBlock',
    image: { id: data.imageId ?? '' },
    backgroundColor: data.background ?? 'white',
    buttonBlocks: data.buttonBlocks ? data.buttonBlocks.map((buttonBlock: any) => ({
      title: buttonBlock.fields.title,
      linkTo: buttonBlock.fields.linkTo,
      kind: buttonBlock.fields.kind,
      size: buttonBlock.fields.size,
      fit: buttonBlock.fields.fit,
      productTheme: buttonBlock.fields.productTheme === 'loyalty' ? 'lux-plus' : buttonBlock.fields.productTheme,
    })) : [],
    buttonDirection: data.buttonDirection || 'horizontal',
  }
}

export function mapContentfulWebPageModalComponent(data: ContentfulWebPageModalComponentData): App.ContentfulWebPageModalComponent {
  switch (data.contentTypeId) {
    case 'componentForm':
      return mapComponentForm(data)
    case 'componentContentModal': {
      return {
        contentTypeId: 'componentContentModal',
        title: data.title ?? '',
        image: { id: data.imageId },
        bodyText: data.bodyText ?? '',
        slug: data.slug,
      }
    }
    default:
      return {
        title: getUnknownComponentTitle(),
        contentTypeId: 'componentNotFound',
        slug: 'unknown-component',
      }
  }
}

function mapContentfulWebPageImageTile(data: any): App.ComponentImageTileType {
  if (data.sys.contentType.sys.id === 'componentImageTextTile') {
    return {
      contentTypeId: 'componentImageTextTile',
      tag: data.fields.tag,
      title: data.fields.title,
      subtitle: data.fields.subtitle,
      linkTo: data.fields.linkTo,
      linkText: data.fields.linkText,
      image: { id: data.fields.imageId },
    }
  }
  return {
    contentTypeId: 'componentImageTile',
    image: { id: data.fields.imageId },
    title: data.fields.title,
    subtitle: data.fields.subtitle,
    linkTo: data.fields.linkTo,
    linkText: data.fields.linkText,
  }
}

function mapDynamicBanner(data: any): App.ComponentDynamicBanner {
  return {
    title: data.fields.title ?? '',
    image: { id: data.fields.imageId },
    linkTo: data.fields.linkTo,
    backgroundColor: data.fields.backgroundColor,
    footerText: data.fields.footerText,
    footerSubText: data.fields.footerSubText,
    labels: data.fields.labels ? data.fields.labels.map((label: string) => label) : undefined,
  }
}

function isPartialVideoBlockFields(value: unknown): value is Partial<IComponentVideoBlockFields> {
  return typeof value === 'object' && value !== null
}

function mapComponentForm(data: Flattened<IComponentFormFields>): App.ComponentForm {
  const rawPostSubmissionStateFields = data.postSubmissionState?.fields
  const postSubmissionStateFields = isPartialVideoBlockFields(rawPostSubmissionStateFields) ?
    rawPostSubmissionStateFields :
    undefined

  return {
    contentTypeId: 'componentForm',
    title: data.title ?? '',
    subtitle: data.subtitle ?? '',
    buttonText: data.buttonText,
    successMessage: data.successMessage,
    backgroundColor: data.backgroundColor as App.ComponentForm['backgroundColor'] ?? 'white',
    slug: data.slug,
    components: (data.components ?? []).map((component: any) => ({
      type: component.fields.type,
      label: component.fields.label,
      placeholder: component.fields.placeholder,
      isRequired: component.fields.isRequired,
      options: component.fields.options,
    })),
    gridComponents: (data.gridComponents ?? []).map((component: any) => ({
      title: component.fields.title,
      image: { id: component.fields.imageId },
      linkTo: component.fields.linkTo,
      linkText: component.fields.linkText,
    })),
    loginRequired: !!data.loginRequired,
    loginRequiredButtonText: data.loginRequiredButtonText,
    postSubmissionState: postSubmissionStateFields
      ? mapContentfulVideoBlock({
          ...postSubmissionStateFields,
          title: postSubmissionStateFields.title
            ? renderMarkdown(postSubmissionStateFields.title)
            : '',
          contentTypeId: 'componentVideoBlock',
        } as Flattened<IComponentVideoBlockFields>)
      : undefined,
  }
}

export function mapContentfulWebPageCardItem(data: any): App.ContentfulWebPageCardItem {
  switch (data.sys.contentType.sys.id) {
    case 'componentPopularRegionTile':
      return {
        title: data.fields.title,
        imageId: data.fields.imageId,
        description: data.fields.description,
        url: data.fields.linkTo,
        type: data.sys.contentType.sys.id,
      }
    case 'componentWhyWeLoveTile':
      return {
        title: data.fields.title,
        imageId: data.fields.imageId,
        description: data.fields.description,
        type: data.sys.contentType.sys.id,
      }
    case 'componentBlogCard':
      return {
        type: data.sys.contentType.sys.id,
        title: data.fields.title,
        imageId: data.fields.imageId,
        description: data.fields.description,
        slug: data.fields.slug,
        date: data.fields.date,
      }
  }
  return {
    title: data.fields.title,
    imageId: data.fields.imageId,
    description: data.fields.description,
    url: data.fields.linkTo,
    type: data.sys.contentType.sys.id,
    slug: data.fields.slug,
    date: data.fields.date,
  }
}

function mapCompetitionStatusPromoPrize(data: any): Omit<App.MarketingCompetitionUserStatusPromo, 'alreadyHasReward'> {
  return {
    competitionType: 'promo',
    codeName: data.codeName,
    promoId: data.promoId,
    promoType: data.promoType,
    expiryDate: data.expiryDate,
    discountValue: data.discountValue,
    minSpend: data.minSpend,
    maxDiscount: data.maxDiscount,
  }
}

function mapComponentValuePropositionItems(item: any): App.ComponentValuePropositionBlock {
  return {
    title: item.fields.title,
    subtitle: item.fields.subtitle,
    icon: item.fields.icon,
    linkText: item.fields.linkText,
    linkTo: item.fields.linkTo,
  }
}
